import React, { useEffect, useRef } from 'react';
import './BarVisualizer.css';

/**
 * Bar Visualizer Component
 * Real-time audio frequency visualizer with state-based animations
 * 
 * @param {string} state - Agent state: 'connecting' | 'initializing' | 'listening' | 'speaking' | 'thinking'
 * @param {number} barCount - Number of bars to display (default: 15)
 * @param {MediaStream} mediaStream - Audio stream for real-time visualization
 * @param {number} minHeight - Minimum bar height as percentage (default: 20)
 * @param {number} maxHeight - Maximum bar height as percentage (default: 100)
 * @param {boolean} demo - Enable demo mode with fake audio data (default: false)
 * @param {boolean} centerAlign - Align bars from center instead of bottom (default: false)
 */
export const BarVisualizer = ({
  state = 'listening',
  barCount = 15,
  mediaStream = null,
  minHeight = 20,
  maxHeight = 100,
  demo = false,
  centerAlign = false,
  className = '',
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const analyserRef = useRef(null);
  const audioContextRef = useRef(null);
  const dataArrayRef = useRef(null);
  const barsRef = useRef([]);

  // Initialize audio analysis if mediaStream is provided
  useEffect(() => {
    if (!mediaStream || demo) return;

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = audioContext.createAnalyser();
    const source = audioContext.createMediaStreamSource(mediaStream);

    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    analyserRef.current = analyser;
    audioContextRef.current = audioContext;
    dataArrayRef.current = dataArray;

    return () => {
      if (source) source.disconnect();
      if (audioContext.state !== 'closed') {
        audioContext.close();
      }
    };
  }, [mediaStream, demo]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    // Initialize bars
    if (barsRef.current.length === 0) {
      barsRef.current = Array.from({ length: barCount }, () => ({
        height: minHeight,
        targetHeight: minHeight,
        velocity: 0,
      }));
    }

    const animate = () => {
      const rect = canvas.getBoundingClientRect();
      // Clear with light gray background
      ctx.fillStyle = '#F3F4F6';
      ctx.fillRect(0, 0, rect.width, rect.height);

      const barWidth = rect.width / barCount;
      const gap = barWidth * 0.15;
      const actualBarWidth = barWidth - gap;
      const maxBarHeight = (rect.height * maxHeight) / 100;
      const minBarHeight = (rect.height * minHeight) / 100;

      // Get frequency data if available
      if (analyserRef.current && dataArrayRef.current && !demo) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);
        const frequencyData = dataArrayRef.current;
        
        // Map frequency data to bars
        barsRef.current.forEach((bar, i) => {
          const dataIndex = Math.floor((i / barCount) * frequencyData.length);
          const value = frequencyData[dataIndex] || 0;
          const normalizedValue = value / 255;
          bar.targetHeight = minBarHeight + normalizedValue * (maxBarHeight - minBarHeight);
        });
      } else {
        // Demo mode - animate based on state with wave pattern
        barsRef.current.forEach((bar, i) => {
          const time = Date.now() * 0.001;
          const normalizedIndex = i / (barCount - 1); // 0 to 1
          
          // Create wave pattern: low at edges, peak in center
          const wavePosition = Math.sin(normalizedIndex * Math.PI); // 0 at edges, 1 at center
          
          let target = minBarHeight;
          let animationIntensity = 0.3;

          if (state === 'speaking') {
            const wave = Math.sin(time * 2 + i * 0.5) * 0.5 + 0.5;
            animationIntensity = 0.9;
            target = minBarHeight + wave * wavePosition * (maxBarHeight - minBarHeight) * animationIntensity;
          } else if (state === 'listening') {
            const wave = Math.sin(time * 0.8 + i * 0.3) * 0.3 + 0.3;
            animationIntensity = 0.4;
            target = minBarHeight + wave * wavePosition * (maxBarHeight - minBarHeight) * animationIntensity;
          } else if (state === 'thinking') {
            const wave = Math.abs(Math.sin(time * 1.2 + i * 0.4));
            animationIntensity = 0.6;
            target = minBarHeight + wave * wavePosition * (maxBarHeight - minBarHeight) * animationIntensity;
          } else if (state === 'connecting' || state === 'initializing') {
            const wave = Math.sin(time * 0.5 + i * 0.2) * 0.2 + 0.2;
            animationIntensity = 0.3;
            target = minBarHeight + wave * wavePosition * (maxBarHeight - minBarHeight) * animationIntensity;
          }

          // Ensure minimum height for visibility
          target = Math.max(minBarHeight, target);
          bar.targetHeight = target;
        });
      }

      // Smooth animation with spring physics
      barsRef.current.forEach((bar) => {
        const diff = bar.targetHeight - bar.height;
        bar.velocity += diff * 0.15;
        bar.velocity *= 0.75; // Damping
        bar.height += bar.velocity;

        // Clamp height
        bar.height = Math.max(minBarHeight, Math.min(maxBarHeight, bar.height));
      });

      // Draw bars
      barsRef.current.forEach((bar, i) => {
        const x = i * barWidth + gap / 2;
        let y, barHeight;

        if (centerAlign) {
          // Center-aligned bars
          barHeight = bar.height;
          y = (rect.height - barHeight) / 2;
        } else {
          // Bottom-aligned bars
          barHeight = bar.height;
          y = rect.height - barHeight;
        }

        // Determine if this is the highlighted bar (center or slightly left of center)
        const centerIndex = Math.floor(barCount * 0.4); // Slightly left of center
        const isHighlighted = i === centerIndex;

        // Color: light gray for most bars, black for highlighted bar
        const color = isHighlighted ? '#000000' : '#D1D5DB'; // Black for highlighted, light gray for others

        // Draw pill-shaped bar (fully rounded top and bottom)
        ctx.fillStyle = color;
        const radius = actualBarWidth / 2; // Full radius for pill shape
        const centerX = x + actualBarWidth / 2;
        
        // Draw pill-shaped bar with rounded top and bottom
        ctx.beginPath();
        // Top semicircle
        ctx.arc(centerX, y + radius, radius, Math.PI, 0, false);
        // Right side line
        ctx.lineTo(x + actualBarWidth, y + barHeight - radius);
        // Bottom semicircle
        ctx.arc(centerX, y + barHeight - radius, radius, 0, Math.PI, false);
        // Left side line (closes the path)
        ctx.closePath();
        ctx.fill();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [state, barCount, minHeight, maxHeight, demo, centerAlign]);

  return (
    <div className={`bar-visualizer ${className}`}>
      <canvas ref={canvasRef} />
    </div>
  );
};
